// Minimal focus-trap composable. Returns an `activate`/`deactivate` controller
// for trapping tab focus inside an element.
//
// Multiple traps may exist at the same time (e.g. a builds popup open while
// the WeaponPicker modal is also open). The shared registry below enforces a
// single active trap: `activate` suspends any existing trap, and the
// suspended trap is restored on `deactivate` so focus management stacks
// cleanly instead of two Tab handlers fighting each other.

import { nextTick, onBeforeUnmount } from 'vue'
import type { Ref } from 'vue'

export interface FocusTrapController {
  activate: () => void
  deactivate: () => void
}

interface TrapEntry {
  readonly id: symbol
  readonly onKeydown: (event: KeyboardEvent) => void
  readonly onSuspend: () => void
  readonly onResume: () => void
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',')

/**
 * `Element.checkVisibility()` exists in modern browsers and reports
 * `display:none`, `visibility:hidden`, and zero-size boxes as `false`. For
 * older engines without the API we fall back to the CSS-visibility + rect
 * check so the focusable filter behaves the same way.
 */
function isVisible(el: HTMLElement): boolean {
  const check = el.checkVisibility
  if (typeof check === 'function') return check.call(el)
  const style = window.getComputedStyle(el)
  return style.visibility !== 'hidden' && el.getBoundingClientRect().width > 0
}

/**
 * Module-scoped registry of installed traps. The topmost entry (last pushed)
 * is the one currently driving Tab focus; every entry beneath it has its
 * `keydown` listener temporarily removed so only the visible trap intercepts
 * Tab. When the top entry deactivates, the trap that was suspended immediately
 * before it is resumed. This keeps stacked popups (`PlayerCard.buildsPopup`
 * over `WeaponPicker`, etc.) from racing each other for focus without
 * forcing callers to coordinate close-before-open themselves.
 *
 * Invariants preserved by `pushEntry` / `removeEntry`:
 *  - At most ONE trap drives focus at a time, recorded in `activeEntry`.
 *  - `pushEntry` suspends the current driver (no-op when stack is empty)
 *    then makes the new entry the driver.
 *  - `removeEntry` only resumes a new driver when the removed entry was
 *    itself the driver. Removing a non-top (already suspended) entry leaves
 *    focus driving untouched — this is what prevents duplicate listeners
 *    when a suspended middle entry is torn down out of order.
 *  - Each trap's `keydown` listener is installed exactly once on push and
 *    removed exactly once on suspend / deactivation; balance is enforced by
 *    routing both transitions through `activeEntry`.
 */
const stack: TrapEntry[] = []
let activeEntry: TrapEntry | null = null

function pushEntry(entry: TrapEntry): void {
  // Suspend the current driver (if any) so its `keydown` listener stops
  // firing. The new entry takes over as the sole driver.
  if (activeEntry) activeEntry.onSuspend()
  stack.push(entry)
  activeEntry = entry
}

function removeEntry(id: symbol): void {
  const idx = stack.findIndex((e) => e.id === id)
  if (idx === -1) return
  const wasDriver = activeEntry !== null && activeEntry.id === id
  stack.splice(idx, 1)
  if (!wasDriver) {
    // A suspended (non-driving) entry is being torn down. The actual
    // driver above it is unaffected; resume it is a no-op AND would add a
    // duplicate listener — skip it.
    return
  }
  // The driver itself is leaving. Promote the next top (if any) back to
  // driving status and re-attach its `keydown` listener.
  const next = stack[stack.length - 1] ?? null
  activeEntry = next
  if (next) next.onResume()
}

export function useFocusTrap(containerRef: Ref<HTMLElement | null>): FocusTrapController {
  let previouslyFocused: HTMLElement | null = null
  let active = false
  const entryId = Symbol('focus-trap')
  let entry: TrapEntry | null = null

  function getFocusable(): HTMLElement[] {
    const root = containerRef.value
    if (!root) return []
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('aria-hidden') && isVisible(el),
    )
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return
    const focusable = getFocusable()
    if (focusable.length === 0) {
      event.preventDefault()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (!first || !last) return
    // `document.activeElement` is `null` only in detached documents; in a
    // mounted Vue app it is always at least `<body>`. Guard for both so we
    // can never blindly read `.tagName` / `===` on a nullable value.
    const activeEl = document.activeElement
    if (activeEl === null) {
      event.preventDefault()
      return
    }
    if (activeEl === document.body) {
      // No focus inside the trap yet — pull focus to the first focusable.
      event.preventDefault()
      first.focus()
      return
    }
    if (event.shiftKey && activeEl === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && activeEl === last) {
      event.preventDefault()
      first.focus()
    }
  }

  function activate(): void {
    // Idempotent — guards against double installation when a parent toggles
    // the bound `modelValue` repeatedly before `deactivate` runs.
    if (active) return
    const root = containerRef.value
    // Retryable when the container ref hasn't populated yet (e.g. the host
    // element is rendered inside a `v-if` that hasn't flipped on this tick,
    // or a teleport target that mounts after this composable runs). Skip
    // cleanly here — preserving the ability to retry on a later `activate()`
    // — instead of marking `active = true` and then leaking the saved
    // `previouslyFocused` element. The host is expected to call `activate()`
    // again (typically from a `watch(containerRef, ...)` handler or once the
    // `v-model:open` bound flag actually flips).
    if (!root) return
    previouslyFocused = document.activeElement as HTMLElement | null
    active = true
    // Defer focus to the next tick so the element is fully visible (and
    // `getFocusable` reflects post-mount state) before we hand focus to it.
    void nextTick(() => {
      // The trap may have been torn down between scheduling and the tick;
      // bail without moving focus so we never `focus()` on a detached node.
      if (!active) return
      const focusable = getFocusable()
      const target = focusable[0] ?? root
      target.focus()
    })
    entry = {
      id: entryId,
      onKeydown,
      onSuspend: () => {
        document.removeEventListener('keydown', onKeydown)
      },
      onResume: () => {
        document.addEventListener('keydown', onKeydown)
      },
    }
    document.addEventListener('keydown', onKeydown)
    pushEntry(entry)
  }

  function deactivate(): void {
    if (!active) return
    active = false
    const wasDriver = activeEntry !== null && activeEntry.id === entryId
    // Drop our own listener ONLY when we were the driver. When we weren't,
    // the listener was already removed by the suspend that the upper trap
    // triggered on push — calling `removeEventListener` again would still
    // be a safe no-op, but skipping it keeps the rule tight: every add has a
    // matching remove, and never a duplicate remove.
    if (wasDriver) document.removeEventListener('keydown', onKeydown)
    removeEntry(entryId)
    entry = null
    // Only restore focus to the previously-focused element when WE were the
    // driver. If a stacked trap above us is still driving focus, that trap
    // owns the focus and stealing it back here would dump the user out of
    // the modal they were actually interacting with.
    if (wasDriver && previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus()
    }
    previouslyFocused = null
  }

  onBeforeUnmount(deactivate)

  return { activate, deactivate }
}